import { useEffect, useMemo, useState } from 'react'
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import MenuItem from '@mui/material/MenuItem'
import Tab from '@mui/material/Tab'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import AddIcon from '@mui/icons-material/Add'
import DescriptionIcon from '@mui/icons-material/Description'
import {
  metaTypeName,
  patternTypeName,
  useGetAuthoringComponentQuery,
  useGetManifestQuery,
  useListComponentsQuery,
} from '../api/authoring'
import type { ComponentSummary, Manifest } from '../api/authoring'
import { LISTING_EMPTY, LISTING_LAG } from '../model/labels'
import { acceptsUpload, proseOf, typeOf, typeTitle } from '../model/manifest'
import { routes, TYPE_FILTER } from '../model/routes'
import { TypeFace } from './library/TypeFace'
import { isImage, thumbnail } from '../model/derived'
import { CreateComponentDialog } from '../ui/CreateComponentDialog'
import { NewTypeDialog } from '../ui/NewTypeDialog'
import { EmptyState, Loading, QueryError } from '../ui/QueryState'

/**
 * The library: everything the tenant holds, and a control that narrows
 * it to one type.
 *
 * **One screen, and one address.** There were fifteen of these — one per
 * registered type, listed down the sidebar and generated from
 * `manifest.types`. That list was honest about the model and useless as
 * an information architecture: a reader looking for a photo had to know
 * that a photo is a Media, and that Media is filed between Links and
 * Menus. The registry is not a filing cabinet, and the day a sixteenth
 * type is registered it was a sixteenth nav entry.
 *
 * **The type is still read from the manifest, and that property is not
 * negotiable.** The filter below is built from `manifest.types` exactly
 * as the nav list was, so a newly registered type appears in it with no
 * client change — which is what SPEC's acceptance checklist asks for and
 * what a hardcoded list of "the interesting types" would quietly lose.
 * What changed is that the type is something the reader *chooses* rather
 * than somewhere the reader *goes*.
 *
 * It keeps every per-type address working by keeping the type in the
 * URL: `?type=Media` is the old media library, linkable and bookmarkable,
 * and the old path redirects to exactly it.
 *
 * **Two types keep an address of their own** — the meta-type and the
 * pattern type — because they are what an agency *designs*, where
 * everything else is what it *makes*. Both are named by asking the
 * manifest which type declares types and which declares patterns, never
 * by holding a name.
 *
 * What differs between one type's listing and another's is presentation,
 * and it is read from the manifest rather than from a name: a type whose
 * components hold a file is browsed as a grid of thumbnails, and
 * everything else as a list. That is `acceptsUpload` doing a second job,
 * and it is the right question — "does this hold a file" is exactly what
 * decides whether there is anything to look at. Unfiltered, there is no
 * one answer, so the unfiltered listing is a list.
 *
 * **This listing is the one authoring read served from the projection**,
 * so it lags. A component created here is reachable immediately, because
 * the create answers with its whole document and this screen navigates to
 * that rather than going looking; but it may take a moment to appear in
 * the list behind it.
 */
/**
 * What to call the unfiltered listing. Not a type's prose, because it is
 * not a type — it is every type, and the words have to be the reader's
 * rather than the registry's.
 */
const EVERYTHING = { title: 'Component', plural: 'Components', description: undefined, declared: false }

export function Library({
  face = 'components',
  library,
}: {
  face?: 'components' | 'type'
  /**
   * The one type this screen is fixed to, named by what it does rather
   * than by what it is called: the type that declares types, or the one
   * that declares patterns. Absent is the whole library, filtered.
   */
  library?: 'types' | 'patterns'
}) {
  const { tenant = '', type: named } = useParams()
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const manifest = useGetManifestQuery({ tenant })
  const [creating, setCreating] = useState(false)

  // Which type this screen is showing, from the three places it can be
  // said: the path (one type's own page), the screen's own fixity, or
  // the filter. Empty is "every type", which is a listing the surface
  // serves directly — `type` is an optional query parameter on the wire,
  // and absent means every type there too.
  const fixed = library === 'types' ? metaTypeName(manifest.data) : library === 'patterns' ? patternTypeName(manifest.data) : undefined
  const type = named ?? fixed ?? params.get(TYPE_FILTER) ?? ''

  // One entry per page fetched, oldest first. Accumulating this way
  // rather than concatenating rows means each page stays its own cache
  // entry keyed by the cursor that produced it — so paging back and forth
  // costs nothing, and a page is never held in two places at once.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined])
  useEffect(() => setCursors([undefined]), [tenant, type])

  const declared = typeOf(manifest.data, type)
  const grid = acceptsUpload(manifest.data, type)
  // A library of *definitions* creates through its own dialog: a
  // definition is created whole — one with no fields or no intents is
  // refused — and the derived create form draws scalars. Asked of the
  // manifest, never of the type's name.
  const defining = declared?.defines === true
  // What this library is a library *of*, in the type's own words. The
  // heading was the wire name, which reads as a category to somebody who
  // knows the model and as nothing at all to somebody who does not.
  const prose = type ? proseOf(declared, type) : EVERYTHING

  // Every type the manifest declares, in the words each one uses for
  // itself, for the filter and for the create dialog. **Built from the
  // registry, never listed here** — that is the same rule the palette
  // and the uploader follow, and the reason a type registered tomorrow
  // is browsable tomorrow.
  const declaredTypes = useMemo(
    () =>
      [...(manifest.data?.types ?? [])]
        .map((entry) => ({ name: entry.name, prose: proseOf(entry), defines: entry.defines === true }))
        .sort((left, right) => left.prose.plural.localeCompare(right.prose.plural)),
    [manifest.data],
  )

  // Where this library's own listing lives, for the tab that returns to
  // it from a type's page.
  const listingHref =
    library === 'types' ? routes.types(tenant) : library === 'patterns' ? routes.patterns(tenant) : routes.components(tenant, type)

  // Where a tenant-declared type's own declaration lives. The meta-type
  // labels a definition by the type it declares, so the listing already
  // carries the join and no document has to be read to find it — and
  // the query is skipped entirely on the face that does not ask.
  const meta = metaTypeName(manifest.data)
  const definitions = useListComponentsQuery(
    { tenant, type: meta ?? '', limit: 100 },
    { skip: face !== 'type' || !meta || declared?.defines === true },
  )
  // Undefined until the listing has answered, so that a tenant's type is
  // not described as the build's while its definition is looked up; null
  // once it is known there is none.
  const definition =
    definitions.isLoading || definitions.isError
      ? undefined
      : (definitions.data?.components.find((candidate) => candidate.label === type) ?? null)

  if (manifest.isLoading) return <Loading label="Loading…" />
  if (library && !fixed) {
    return (
      <EmptyState title={library === 'types' ? 'This build declares no types' : 'This build declares no patterns'}>
        <Typography color="text.secondary" sx={{ maxWidth: 560 }}>
          No registered type says it declares {library === 'types' ? 'component types' : 'patterns'}, so there is
          nothing for this screen to list. It is asked of the manifest rather than assumed, which is why a build
          without one says so rather than showing an empty list of something.
        </Typography>
      </EmptyState>
    )
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={2} alignItems="flex-end" justifyContent="space-between" flexWrap="wrap">
        <Stack spacing={0.5}>
          <Typography variant="h1">{prose.plural}</Typography>
          <Typography color="text.secondary">
            {defining ? (
              <>
                The component types <strong>{tenant}</strong> has declared for itself. Each one is an ordinary
                component, so its history, its audit and its revert are the ones every other component has.
              </>
            ) : type ? (
              <>
                {prose.description ? `${prose.description} ` : ''}Every one you have, whether it is used on a page yet or
                not.
              </>
            ) : (
              <>
                Everything <strong>{tenant}</strong> holds that stands on its own, of every type, whether it is placed
                anywhere or not. Narrow it with the filter.
              </>
            )}
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1.5} alignItems="center">
          {/* The filter, built from the registry. It is where fifteen nav
              entries went: the same set, offered where the reader is
              already looking at the thing being narrowed, and with an
              "every type" option those entries could not express. */}
          {!library && face === 'components' && (
            <TextField
              select
              size="small"
              label="Type"
              value={type}
              /* "Every type" is the empty value, and MUI reads an empty
                 value as "nothing chosen" — so the label is pinned and
                 the empty option is allowed to draw itself, rather than
                 the control sitting there with its label lying across a
                 choice the reader did make. */
              InputLabelProps={{ shrink: true }}
              SelectProps={{ displayEmpty: true }}
              onChange={(event) => {
                const next = new URLSearchParams(params)
                if (event.target.value) next.set(TYPE_FILTER, event.target.value)
                else next.delete(TYPE_FILTER)
                setParams(next)
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Every type</MenuItem>
              {declaredTypes.map((entry) => (
                <MenuItem key={entry.name} value={entry.name}>
                  {entry.prose.plural}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreating(true)}
            disabled={Boolean(type) && !declared}
          >
            {defining ? 'New type' : type ? `New ${prose.title.toLowerCase()}` : 'New component'}
          </Button>
        </Stack>
      </Stack>

      {/* Two faces: what the tenant holds of this type, and what the
          type *is*. The second was in the manifest all along and drawn
          nowhere — an operator asking "what goes in a Section" had to
          open one and look at its Add button. Links, not buttons, on the
          terms every other face in this client is an address.

          Only where a type is named. "What type is this" has no answer
          over every type at once, and a tab that was sometimes about
          nothing would be worse than an absent one. */}
      {type && (
        <Tabs value={face} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={prose.plural} value="components" component={RouterLink} to={listingHref} />
          <Tab label="Type" value="type" component={RouterLink} to={routes.type(tenant, type)} />
        </Tabs>
      )}

      {face === 'type' ? (
        <TypeFace tenant={tenant} name={type} declared={declared} manifest={manifest.data} definition={definition} />
      ) : (
        <>
      {type && !declared && (
        <Alert severity="warning">
          This build&rsquo;s manifest does not declare <strong>{type}</strong>. The listing below is what the tenant
          index holds under that name, which for a type nobody registers is nothing.
        </Alert>
      )}

      {cursors.map((cursor, index) => (
        <LibraryPage
          key={cursor ?? 'first'}
          tenant={tenant}
          type={type}
          plural={prose.plural}
          manifest={manifest.data}
          cursor={cursor}
          grid={grid}
          first={index === 0}
          last={index === cursors.length - 1}
          onMore={(next) => setCursors([...cursors, next])}
        />
      ))}

      <Typography variant="caption" color="text.secondary">
        {LISTING_LAG}
      </Typography>
        </>
      )}

      {/* Straight to the thing that was just made, from the create's own
          answer. Waiting for it to appear in the listing would be waiting
          on the projection, which is the one read here that does not
          promise to have caught up. */}
      {defining ? (
        <NewTypeDialog
          tenant={tenant}
          metaType={type}
          open={creating}
          manifest={manifest.data}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false)
            navigate(routes.component(tenant, created.id))
          }}
        />
      ) : (
        <CreateComponentDialog
          tenant={tenant}
          open={creating}
          manifest={manifest.data}
          /* One type where one is chosen, and otherwise every type that
             is not a definition — those are created whole, through the
             dialog above. The dialog already offers a type picker when
             it is handed more than one, which is exactly what an
             unfiltered library needs. */
          types={type ? [type] : declaredTypes.filter((entry) => !entry.defines).map((entry) => entry.name)}
          onClose={() => setCreating(false)}
          onCreated={(created) => {
            setCreating(false)
            navigate(routes.component(tenant, created.id))
          }}
        />
      )}
    </Stack>
  )
}

/**
 * One page of the listing.
 *
 * A component per page rather than a merged array, so the "load more"
 * button belongs to the page that issued the cursor — which is also the
 * only page that knows whether the listing continued. **An absent cursor
 * is the only thing that says it is exhausted**; a short page does not,
 * because DynamoDB pages by size as well as by count.
 */
function LibraryPage({
  tenant,
  type,
  plural,
  manifest,
  cursor,
  grid,
  first,
  last,
  onMore,
}: {
  tenant: string
  /** The type being listed, or empty for every type. */
  type: string
  /** What the type calls a set of them, for the sentences on this page. */
  plural: string
  /** For naming each row's type where the listing spans several. */
  manifest: Manifest | undefined
  cursor: string | undefined
  /** Whether components of this type hold a file worth looking at. */
  grid: boolean
  first: boolean
  last: boolean
  onMore: (cursor: string) => void
}) {
  const listing = useListComponentsQuery({
    tenant,
    // Absent lists every type, which is what the unfiltered library is.
    ...(type ? { type } : {}),
    // A smaller page where every row is a document read. A grid draws a
    // thumbnail, and a thumbnail is a fact the *document* carries rather
    // than the listing row — see `LibraryTile` — so the page size is what
    // bounds the fan-out. A list needs no such thing.
    limit: grid ? 24 : 50,
    ...(cursor ? { cursor } : {}),
  })

  if (listing.isLoading) return <Loading label={`Listing ${plural.toLowerCase()}…`} />
  if (listing.error) return <QueryError error={listing.error} title={`Could not list ${plural.toLowerCase()}`} />

  const components = listing.data?.components ?? []

  if (first && components.length === 0) {
    return (
      <EmptyState title={`No ${plural.toLowerCase()} yet`}>
        <Typography color="text.secondary" sx={{ maxWidth: 560 }}>
          {LISTING_EMPTY}
        </Typography>
      </EmptyState>
    )
  }

  return (
    <Stack spacing={2}>
      {grid ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
            gap: 2,
          }}
        >
          {components.map((component) => (
            <LibraryTile key={component.id} tenant={tenant} component={component} />
          ))}
        </Box>
      ) : (
        <Paper>
          <List disablePadding>
            {components.map((component) => (
              <ListItemButton
                key={component.id}
                component={RouterLink}
                to={routes.component(tenant, component.id)}
              >
                <ListItemText
                  primary={component.label ?? component.id}
                  secondary={component.label ? component.id : 'Unnamed'}
                />
                {/* What kind of thing each row is, where the listing is
                    not already one kind. A list of names across every
                    type is otherwise a list of names. */}
                {!type && <Chip size="small" variant="outlined" label={typeTitle(manifest, component.type)} />}
                <Chip size="small" variant="outlined" label={`r${component.revision}`} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      )}

      {last && listing.data?.cursor && (
        <Stack direction="row">
          <Button onClick={() => onMore(listing.data!.cursor!)}>Load more</Button>
        </Stack>
      )}
    </Stack>
  )
}

/**
 * One component in a thumbnail grid.
 *
 * **It reads the document, and that is a deliberate cost.** A listing row
 * carries what a row needs — id, type, label, revision — and a thumbnail
 * is none of those: which renditions exist is a fact the *document*
 * carries, because the platform skips a width above the original and
 * makes none at all for something that is not an image. Putting a
 * thumbnail on the index row would mean the indexer asking a type what
 * its picture is, which is a sixth per-type contribution and belongs to
 * B6 rather than to a grid.
 *
 * So a page of the grid is a page of point reads, which is why the page
 * size is smaller here. They are cached by `(tenant, id)`, so opening one
 * afterwards costs nothing, and an admin tool browsing its own tenant is
 * the one place that trade is clearly the right way round.
 */
function LibraryTile({ tenant, component }: { tenant: string; component: ComponentSummary }) {
  const { data } = useGetAuthoringComponentQuery({ tenant, id: component.id })

  // 320 is the smallest width the platform generates, which is what a
  // tile this size wants. What comes back is a *recorded* rendition or
  // the original — never a URL this client assembled from a width.
  const preview = thumbnail(data, 320)
  const drawable = preview && isImage(data)

  return (
    <Paper
      component={RouterLink}
      to={routes.component(tenant, component.id)}
      sx={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        overflow: 'hidden',
        '&:hover': { borderColor: 'primary.main' },
      }}
    >
      <Box
        sx={{
          height: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          color: 'text.disabled',
        }}
      >
        {drawable ? (
          <Box
            component="img"
            src={preview}
            alt=""
            loading="lazy"
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <DescriptionIcon fontSize="large" />
        )}
      </Box>
      <Stack spacing={0.25} sx={{ p: 1 }}>
        <Typography variant="body2" noWrap title={component.label ?? component.id}>
          {component.label ?? component.id}
        </Typography>
        <Typography variant="caption" color="text.secondary" noWrap>
          {/* Three states behind one sentence, and only the last is a
              defect: nothing uploaded yet, a file this stage names no
              origin for, or something that is not an image. An author
              can act on the first and can do nothing about the other
              two, so the words are the first one's. */}
          {data && !preview ? 'No image uploaded yet' : component.id}
        </Typography>
      </Stack>
    </Paper>
  )
}
